import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import LinearProgress from "@mui/material/LinearProgress";
import Step from "@mui/material/Step";
import { default as MuiStepLabel } from "@mui/material/StepLabel";
import { default as MuiStepper } from "@mui/material/Stepper";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PaymentAmount from "../payment/PaymentAmount";
import { CardPaymentFlow } from "./CardPaymentFlow";
import { ResponseStep } from "./Steps";
import { FormPaymentRequest, GlobalpayPaymentResponse } from "./types";
import { useGlobalpayPaymentFlow } from "./useGlobalpayPaymentFlow";

const stepperSteps = [
  { id: "payment-amount", title: "Payment amount" },
  { id: "enter-card-details", title: "Enter card details" },
  { id: "payment-response", title: "Payment response" },
];

const Stepper: React.FC<{ children: React.ReactNode; activeStep: number }> = ({
  children,
  activeStep,
}) => (
  <MuiStepper
    alternativeLabel
    sx={{
      my: 3,
      "& .MuiStepConnector-root": {
        top: "21px",
        left: "calc(-50% + 35px)",
        right: "calc(50% + 35px)",
      },
      "& .MuiStepConnector-root-line": {
        right: "35px",
        left: "35px",
        flex: "1 1 auto",
        position: "absolute",
        top: "21px",
      },
    }}
    activeStep={activeStep}
  >
    {children}
  </MuiStepper>
);

const StepLabel: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <MuiStepLabel
    StepIconProps={{
      sx: {
        fontSize: "45px",
        "&.Mui-active": {
          color: "primary.dark",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
    }}
    sx={{
      "& .MuiStepLabel-labelContainer": {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      },
      "& .MuiStepLabel-root": {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      },
    }}
  >
    {children}
  </MuiStepLabel>
);



const GlobalpayPayment: React.FC = () => {
  const [stepperActiveStep, setStepperActiveStep] = useState(0);
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [stepperActiveStep]);

  const [form, setForm] = useState<FormPaymentRequest>({
    amount: "",
    name: "",
    postcode: "",
    address: "",
    cardNumber: "",
    expiryMonth: "",
    expiryYear: "",
    securityCode: "",
    startMonth: "",
    startYear: "",
  });
  const navigate = useNavigate();
  const [response, setResponse] = useState<GlobalpayPaymentResponse | null>(
    null
  );

  const {
    accountReference,
    currentBalance,
    loadingContext,
    submitPayment,
    continuePayment,
  } = useGlobalpayPaymentFlow();

  if (loadingContext) {
    return <LinearProgress />;
  }

  return (
    <Container sx={{ mt: 4, mb: 7, paddingBottom: "350px" }}>
      <Box sx={{ width: "100%", marginTop: 2, marginBottom: 35 }}>
        <Stepper activeStep={stepperActiveStep}>
          {stepperSteps.map((step) => (
            <Step id={step.id} key={step.title}>
              <StepLabel>{step.title}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <Box sx={{ textAlign: "center" }}>
          <>
            {stepperActiveStep === 0 && (
              <PaymentAmount
                handleNext={() => setStepperActiveStep(1)}
                balance={currentBalance}
                paymentAmount={form.amount}
                updatePaymentAmount={(amount: string) =>
                  setForm({ ...form, amount })
                }
              />
            )}
            {stepperActiveStep === 1 && (
              <CardPaymentFlow
                form={form}
                setForm={setForm}
                onBack={() => setStepperActiveStep(0)}
                onComplete={(resp: GlobalpayPaymentResponse) => {
                  setResponse(resp);
                  setStepperActiveStep(2);
                }}
                submitPayment={submitPayment}
                continuePayment={continuePayment}
              />
            )}
            {stepperActiveStep === 2 && (
              <ResponseStep
                accountReference={accountReference}
                response={response}
                amount={form.amount}
                onTryAgain={() => {
                  setStepperActiveStep(1);
                  setResponse(null);
                }}
                onComplete={() => {
                  navigate("/");
                  window.location.reload();
                }}
              />
            )}
          </>
        </Box>
      </Box>
    </Container>
  );
};

export default GlobalpayPayment;